import React from "react";
import {Citation} from "./inline/Citation";
import {ArtifactReference} from "./inline/ArtifactReference";
import {stripEngineMarkers} from "../../engineCitations";

// Clean up [Ln] style citations:
// - [0 L12][0 L13] → [0] (deduplicate after stripping)
// - [0 L12] → [0] (keep the numeric ref, strip the L-part)
// - [L12] → "" (standalone L-citations are removed entirely)
export const stripLCitations = (text: string): string =>
    outsideCode(text, (segment) =>
        stripEngineMarkers(segment)
            .replace(/\[(\d+)\s+L\d+\]/g, "[$1]")
            .replace(/\s*\[L\d+\]/g, "")
            .replace(/(\[\d+\])(\s*\1)+/g, "$1")
    );

// Code keeps its backslashes and dollars: a fenced block, a fence that still streams, or an
// inline span. String.split keeps these segments, and the math rewrites do not change them.
// Code indented by four spaces is not included. An indent rule would also select nested list
// items, and models write fenced blocks.
const CODE_SEGMENT =
    /(```[\s\S]*?```|~~~[\s\S]*?~~~|```[\s\S]*$|~~~[\s\S]*$|`[^`\n]+`)/;

/**
 * Applies the transform to all text outside code. The transform also receives the offset of
 * its segment in the full text. A rewrite that depends on the lines around it needs that
 * offset.
 */
export const outsideCode = (
    text: string,
    transform: (segment: string, segmentStart: number) => string
): string => {
    let consumed = 0;
    return text
        .split(CODE_SEGMENT)
        .map((segment, index) => {
            const segmentStart = consumed;
            consumed += segment.length;
            return index % 2 === 1 ? segment : transform(segment, segmentStart);
        })
        .join("");
};

// Dollar delimited math. normalizeMathDelimiters rewrites the bracket delimiters into this
// form. A [1] in such a segment is part of the formula, not a source.
const MATH_SEGMENT = /(\$\$[\s\S]*?\$\$|\$[^$\n]+\$)/;

// Escape [n] citation and [aN] artifact brackets so markdown doesn't parse them as link references.
// \[1\] is rendered by markdown as literal [1] text, which processReferences can handle.
export const escapeCitationBrackets = (text: string): string =>
    outsideCode(text, (segment) =>
        segment
            .split(MATH_SEGMENT)
            .map((part, index) =>
                index % 2 === 1
                    ? part
                    : part
                          .replace(/\[(\d+)\]/g, "\\[$1\\]")
                          .replace(/\[(a\d+)\]/g, "\\[$1\\]")
            )
            .join("")
    );

// A complete pair of LaTeX bracket delimiters: \[display\] or \(inline\). The body can span
// lines, but not a blank line. An opener that never closes therefore cannot pair with the
// closing delimiter of the next formula and include the paragraphs between them. The pattern
// accepts both line endings, because a CRLF answer must also obey the blank line rule.
const BRACKET_MATH =
    /\\\[((?:[^\r\n]|\r?\n(?![ \t]*\r?\n))+?)\\\]|\\\(((?:[^\r\n]|\r?\n(?![ \t]*\r?\n))+?)\\\)/g;

// \[1\] and \[a2\] are a citation or an artifact marker the model escaped itself, not math.
const REFERENCE_BODY = /^\s*a?\d+\s*$/;

// A display delimiter at the start of a line. This form becomes a $$ block.
const OWN_LINE_BRACKET_OPEN = /^[ \t]*\\\[/gm;

// A line whose content starts with $$, the form that can open a display block. Blockquote
// markers count as part of the prefix, because the quote strips them before the block rules
// apply.
const FENCE_LINE = /^[ \t>]*\$\$/;

// A line that holds nothing but a fence, the only form that closes a display block. A fence
// with anything after it on the line, such as $$ tail, leaves the block open.
const FENCE_ONLY_LINE = /^[ \t>]*\$\$[ \t]*\r?$/;

// An opening fence with formula text after it on the same line, such as $$ a = b. The
// prefix is the indentation or quote marker before the fence.
const FENCE_WITH_TAIL = /^([ \t>]*)\$\$[ \t]*(\S.*?)[ \t]*(\r?)$/;

// A closing fence with formula text before it on the same line, such as c + d $$.
const FENCE_WITH_HEAD = /^([ \t>]*)(.*\S)[ \t]*\$\$[ \t]*(\r?)$/;

// A line with no content. A display block does not span one.
const BLANK_LINE = /^[ \t>]*\r?$/;

// Any delimiter that can start math. The component uses it to decide when to load KaTeX.
const MATH_DELIMITERS = /\$\$|\\\(|\\\[|\$[^$\n]+\$/;

/**
 * Moves a display fence that shares its line with formula text onto its own line. A model
 * that wraps a long formula writes the fences as $$ a, then the terms, then b $$. The
 * markdown parser reads a fence like a code fence: it drops the text after an opener as
 * meta, and a closer with text before it closes nothing. The block then swallows the rest
 * of the answer, or resolveUnclosedDisplayMath escapes the opener and markdown reads a lone
 * = or - on the next line as a heading underline.
 *
 * A block ends at the first later line that holds $$. The fence moves only when that line
 * closes the block: a bare $$, or text that ends with $$. A line with $$ elsewhere leaves
 * the block as it is. A blank line ends the search, so an opener that still streams keeps
 * its tail until the closer arrives. A formula on one line opens no block.
 */
export const normalizeDisplayFences = (text: string): string =>
    outsideCode(text, (segment, segmentStart) => {
        const lines = segment.split("\n");
        // A segment that follows an inline code span starts in the middle of a line. That
        // line has no line start, so it opens nothing.
        let i = segmentStart > 0 && text[segmentStart - 1] !== "\n" ? 1 : 0;
        while (i < lines.length) {
            const opener = FENCE_LINE.exec(lines[i]);
            if (!opener || lines[i].includes("$$", opener[0].length)) {
                i += 1;
                continue;
            }
            let j = i + 1;
            while (
                j < lines.length &&
                !BLANK_LINE.test(lines[j]) &&
                !lines[j].includes("$$")
            ) {
                j += 1;
            }
            if (j >= lines.length || !lines[j].includes("$$")) {
                i = j;
                continue;
            }
            // A bare fence is tested first: on a quoted "> $$" the head pattern would read
            // the ">" as formula text. A line with a second $$ holds inline math.
            const bare = FENCE_ONLY_LINE.test(lines[j]);
            const single = lines[j].indexOf("$$") === lines[j].lastIndexOf("$$");
            const head = bare || !single ? null : FENCE_WITH_HEAD.exec(lines[j]);
            if (!bare && !head) {
                i = j + 1;
                continue;
            }
            if (head) {
                lines.splice(j, 1, `${head[1]}${head[2]}${head[3]}`, `${head[1]}$$${head[3]}`);
                j += 1;
            }
            const tail = FENCE_WITH_TAIL.exec(lines[i]);
            if (tail) {
                lines.splice(i, 1, `${tail[1]}$$${tail[3]}`, `${tail[1]}${tail[2]}${tail[3]}`);
                j += 1;
            }
            i = j + 1;
        }
        return lines.join("\n");
    });

/**
 * Returns the indentation of the line when the text from start to end is alone on that line.
 * Returns null when other text shares the line.
 */
const ownLineIndent = (
    text: string,
    start: number,
    end: number
): string | null => {
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = text.indexOf("\n", end);
    const indent = text.slice(lineStart, start);
    const rest = text.slice(end, lineEnd === -1 ? undefined : lineEnd);
    return indent.trim() === "" && rest.trim() === "" ? indent : null;
};

/**
 * Rewrites LaTeX bracket delimiters as dollar delimiters. remark-math reads only dollars,
 * and models write \(x\) and \[x\] as often as $x$. A pair that is not complete stays as it
 * is. It becomes math when its closing delimiter arrives.
 *
 * Citation brackets [n], artifact markers [aN] and markdown links have no backslash, so they
 * do not match. \[x\] becomes a display block only on its own line. In a paragraph markdown
 * reads $$x$$ as inline math, so \[x\] becomes $x$ there.
 *
 * Each line of a display block keeps the indentation of the line it replaces. A block that
 * starts in column 0 inside a list item ends the list. The rest of the answer then becomes
 * one formula that never closes.
 */
export const normalizeMathDelimiters = (text: string): string =>
    outsideCode(text, (segment, segmentStart) =>
        segment.replace(
            BRACKET_MATH,
            (
                match: string,
                display: string | undefined,
                inline: string,
                offset: number
            ) => {
                if (REFERENCE_BODY.test(display ?? inline)) {
                    return match;
                }
                if (display === undefined) {
                    return `$${inline}$`;
                }
                const start = segmentStart + offset;
                const indent = ownLineIndent(text, start, start + match.length);
                if (indent === null) {
                    return `$${display}$`;
                }
                const body = display
                    .trim()
                    .split("\n")
                    .map((line) => `${indent}${line.trim()}`)
                    .join("\n");
                return `$$\n${body}\n${indent}$$`;
            }
        )
    );

/** Replaces the text that the pattern captures with spaces. The length and the lines stay. */
const blankSegments = (text: string, pattern: RegExp): string =>
    text
        .split(pattern)
        .map((segment, index) =>
            index % 2 === 1 ? segment.replace(/[^\n]/g, " ") : segment
        )
        .join("");

/** Replaces code with spaces. The length and the lines stay, so offsets remain correct. */
const blankOutCode = (text: string): string => blankSegments(text, CODE_SEGMENT);

/**
 * Replaces code and dollar math with spaces, so a position in the result is the same position
 * in the original text. The math step reads the code blanked text, so a dollar sign inside
 * code opens no formula.
 */
export const blankOutCodeAndMath = (text: string): string =>
    blankSegments(blankOutCode(text), MATH_SEGMENT);

/**
 * Finds the display math openers that are missing their closing delimiter: a $$ block that
 * no later fence closes, and an own-line \[ with no \] after it.
 *
 * The scan reads the fences line by line, in the same way as the markdown parser. It does
 * not count them. A line that starts with $$ and holds a second $$ is inline math, not a
 * block. $$x = 1$$ on one line therefore opens nothing. A count would read that formula as
 * an unclosed opener. Only a line that holds nothing but a fence closes a block.
 *
 * The offsets are into the original text, because blankOutCode keeps its length.
 */
const findUnclosedDisplayMath = (
    text: string
): {fence: number | null; bracket: number | null} => {
    const scannable = blankOutCode(text);

    let fence: number | null = null;
    let lineStart = 0;
    for (const line of scannable.split("\n")) {
        const opener: RegExpExecArray | null =
            fence === null ? FENCE_LINE.exec(line) : null;
        if (opener && line.indexOf("$$", opener[0].length) === -1) {
            fence = lineStart + opener[0].length - "$$".length;
        } else if (fence !== null && FENCE_ONLY_LINE.test(line)) {
            fence = null;
        }
        lineStart += line.length + "\n".length;
    }

    const openers = [...scannable.matchAll(OWN_LINE_BRACKET_OPEN)];
    const lastOpener = openers[openers.length - 1];
    const openerStart = lastOpener
        ? lastOpener.index + lastOpener[0].length - "\\[".length
        : null;
    const bracket =
        openerStart !== null && !scannable.includes("\\]", openerStart)
            ? openerStart
            : null;

    return {fence, bracket};
};

/**
 * Makes a display math block safe when its closing delimiter is missing. The treatment
 * depends on the state of the answer.
 *
 * While the answer streams, this function removes the block. If the block stays, remark-math
 * sets the full rest of the answer as one formula on each animation frame. After the removal
 * the block becomes math when its closing delimiter arrives. stripUnclosedCustomTags treats
 * an incomplete tag in the same way.
 *
 * In a complete answer no closing delimiter can arrive. A removal would hide all text after
 * the delimiter, so this function escapes the $$ instead. The reader then sees the two
 * dollars as text. A single \[ needs no escape. normalizeMathDelimiters rewrites complete
 * pairs only, so markdown shows the \[ as text.
 *
 * The typewriter shows only a start of the finished text. The caller then passes the
 * finished text as fullText. An opener is settled, and escaped, only when it is also the
 * unclosed opener of fullText. An opener whose closer is in fullText but not yet typed
 * stays removed until the closer is typed.
 */
export const resolveUnclosedDisplayMath = (
    text: string,
    isComplete: boolean,
    fullText: string = text
): string => {
    const {fence, bracket} = findUnclosedDisplayMath(text);
    const settled = isComplete ? findUnclosedDisplayMath(fullText) : null;
    const fenceSettled = fence !== null && settled?.fence === fence;
    const bracketSettled = bracket !== null && settled?.bracket === bracket;

    const cut = Math.min(
        fence !== null && !fenceSettled ? fence : text.length,
        bracket !== null && !bracketSettled ? bracket : text.length
    );
    const kept = text.slice(0, cut);

    return fenceSettled && fence < cut
        ? `${kept.slice(0, fence)}\\$\\$${kept.slice(fence + "$$".length)}`
        : kept;
};

/** Returns whether the text can contain math. A false positive loads KaTeX without need. */
export const containsMathDelimiters = (text: string): boolean =>
    MATH_DELIMITERS.test(text);

// Fix broken nested markdown links: [any label]([Title](url)) → [Title](url)
// LLMs sometimes wrap a valid markdown link inside another link's URL.
export const fixNestedMarkdownLinks = (text: string): string =>
    text.replace(
        /\[[^\]]+\]\((\[[^\]]+\]\([^)]+\))\)/g,
        "$1",
    );

// Process standalone references that aren't attached to a word
const processStandaloneRefs = (
    text: string,
    keyPrefix: string
): React.ReactNode[] => {
    const cleaned = stripLCitations(text);
    const parts = cleaned.split(/(\[\d+\]|\[a\d+\])/g);
    return parts
        .filter((p) => p !== "")
        .map((part, index) => {
            if (part.match(/^\[\d+\]$/)) {
                return (
                    <Citation key={`${keyPrefix}-${index}`} reference={part} />
                );
            }
            if (part.match(/^\[a\d+\]$/)) {
                return (
                    <ArtifactReference key={`${keyPrefix}-${index}`} reference={part} />
                );
            }
            return part;
        });
};

// Process string content to group words with their following references
// This prevents line breaks between a word and its citations (e.g., "word[1][2]" or "word [1][2]")
const processStringContent = (text: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    const cleaned = stripLCitations(text);
    let lastIndex = 0;

    // Pattern matches: word followed by (optional space +) one or more references (numeric or artifact)
    // - ([^\s\[\]]+) captures the word (no brackets or whitespace)
    // - (\s?) captures optional space between word and first ref
    // - ((?:\[\d+\]|\[a\d+\])(?:\s*(?:\[\d+\]|\[a\d+\]))*) captures refs (with optional spaces between)
    const wordWithRefsPattern =
        /([^\s\[\]]+)(\s?)((?:\[\d+\]|\[a\d+\])(?:\s*(?:\[\d+\]|\[a\d+\]))*)/g;
    let match;

    while ((match = wordWithRefsPattern.exec(cleaned)) !== null) {
        // Add text before this match, processing any standalone references
        if (match.index > lastIndex) {
            result.push(
                ...processStandaloneRefs(
                    cleaned.slice(lastIndex, match.index),
                    `pre-${match.index}`
                )
            );
        }

        const word = match[1];
        const space = match[2];
        const refs = match[3];

        // Split refs into individual citations or artifact references, keeping the whitespace that
        // separated each one. An artifact reference renders its name as prose, so a dropped
        // separator reads as "regnskapsloven¹E-postutkast" rather than as two things.
        const isArtifactRef = (ref: string) => ref.startsWith("[a");
        const refTokens: {ref: string; gap: string}[] = [];
        let consumed = 0;
        for (const refMatch of refs.matchAll(/\[\d+\]|\[a\d+\]/g)) {
            refTokens.push({
                ref: refMatch[0],
                gap: refs.slice(consumed, refMatch.index),
            });
            consumed = refMatch.index + refMatch[0].length;
        }

        let cursor = 0;
        const gluedCitations: {ref: string; gap: string}[] = [];
        while (cursor < refTokens.length && !isArtifactRef(refTokens[cursor].ref)) {
            gluedCitations.push(refTokens[cursor]);
            cursor++;
        }

        if (gluedCitations.length > 0) {
            result.push(
                <span key={`${match.index}-glued`} style={{whiteSpace: "nowrap"}}>
                    {word}
                    {space}
                    {gluedCitations.map((token, i) => (
                        <React.Fragment key={i}>
                            {token.gap}
                            <Citation reference={token.ref} />
                        </React.Fragment>
                    ))}
                </span>
            );
        } else {
            result.push(word);
            if (space) result.push(space);
        }

        for (let i = cursor; i < refTokens.length; i++) {
            const {ref, gap} = refTokens[i];
            // Outside the nowrap span, so this separator is also the wrap opportunity before a
            // possibly sentence-long artifact name.
            if (gap) result.push(gap);
            result.push(
                isArtifactRef(ref) ? (
                    <ArtifactReference key={`${match.index}-ref-${i}`} reference={ref} />
                ) : (
                    <Citation key={`${match.index}-ref-${i}`} reference={ref} />
                )
            );
        }

        lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match, processing any standalone references
    if (lastIndex < cleaned.length) {
        result.push(
            ...processStandaloneRefs(
                cleaned.slice(lastIndex),
                `post-${lastIndex}`
            )
        );
    }

    // If no matches found, process entire text for standalone refs
    if (result.length === 0) {
        return processStandaloneRefs(cleaned, "standalone");
    }

    return result;
};

/** Returns a mapping from original citation indices to sequential ones, or null if already sequential. */
export function buildCitationMapping(citations: number[]): Map<number, number> | null {
    if (citations.length === 0) return null;
    const sorted = [...citations].sort((a, b) => a - b);
    if (sorted.every((v, i) => v === i)) return null;
    return new Map(sorted.map((original, sequential) => [original, sequential]));
}

/** Replaces [n] citation markers in text using the mapping. */
export function renumberTextCitations(text: string, mapping: Map<number, number>): string {
    return text.replace(/\[(\d+)\]/g, (match, num) => {
        const mapped = mapping.get(parseInt(num));
        return mapped !== undefined ? `[${mapped}]` : match;
    });
}

// This helper function processes inline references (like [1], [2], etc.)
export const processReferences = (
    content: React.ReactNode
): React.ReactNode => {
    if (typeof content !== "string") {
        if (React.isValidElement(content)) {
            return React.cloneElement(content, {
                children: processReferences(content.props.children),
            });
        }
        if (Array.isArray(content)) {
            return content.map((child, index) => (
                <React.Fragment key={index}>
                    {processReferences(child)}
                </React.Fragment>
            ));
        }
        return content;
    }

    return processStringContent(content);
};
